import React, { useCallback, useEffect, useState } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import API from '../../component/Axios'
import Pagination from '../../component/Pagination'
import Spiner from '../../component/Spiner'

const PRODUCT_IMAGE_SX = {
    width: 48,
    height: 48,
    objectFit: 'contain',
    display: 'block',
}

const OFFSET = 10

// 경품 규칙에 넣을 기프티콘 상품을 고른다. 판매 중(SALE)인 상품만 선택할 수 있다.
export default function GifticonProductPickerDialog({ open, onClose, onSelect }) {
    const [loading, setLoading] = useState(false)
    const [searchText, setSearchText] = useState('')
    const [searchQuery, setSearchQuery] = useState('')
    const [curPage, setCurPage] = useState(1)
    const [totalCount, setTotalCount] = useState(0)
    const [rows, setRows] = useState([])
    const [errorMessage, setErrorMessage] = useState('')

    const getData = useCallback(async () => {
        setLoading(true)
        setErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/gifticon/getList', {
                page: curPage,
                offset: OFFSET,
                searchText: searchQuery,
                // 활성화된 상품만 조회하고, 판매중(SALE)인지는 목록에서 한 번 더 걸러낸다.
                searchSelect: { searchStatus: 1 },
            })

            if (data.code === 200) {
                setTotalCount(data.data.count || 0)
                setRows((data.data.rows || []).filter((row) => String(row.goodsStateCd).toUpperCase() === 'SALE'))
            } else {
                setRows([])
                setTotalCount(0)
                setErrorMessage(data.message || '기프티콘 상품을 불러오지 못했습니다.')
            }
        } catch (_error) {
            setRows([])
            setTotalCount(0)
            setErrorMessage('기프티콘 상품을 불러오지 못했습니다.')
        } finally {
            setLoading(false)
        }
    }, [curPage, searchQuery])

    useEffect(() => {
        if (!open) {
            return
        }

        getData()
    }, [getData, open])

    const handleSearchSubmit = (event) => {
        event.preventDefault()
        setCurPage(1)
        setSearchQuery(searchText.trim())
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>기프티콘 상품 선택</DialogTitle>
            <DialogContent dividers>
                <Box component="form" onSubmit={handleSearchSubmit} sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <TextField
                        size="small"
                        fullWidth
                        label="상품명 · 브랜드"
                        value={searchText}
                        onChange={(event) => setSearchText(event.target.value)}
                    />
                    <Button type="submit" variant="contained" size="small">검색</Button>
                </Box>

                {loading ? <Spiner /> : (
                    <>
                        {errorMessage && (
                            <Typography variant="body2" color="error" sx={{ mb: 1 }}>{errorMessage}</Typography>
                        )}
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>이미지</TableCell>
                                    <TableCell>브랜드</TableCell>
                                    <TableCell>상품명</TableCell>
                                    <TableCell align="right">판매가</TableCell>
                                    <TableCell align="right">선택</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.length > 0 ? rows.map((row) => (
                                    <TableRow key={row.goodsCode}>
                                        <TableCell>
                                            {row.goodsImgS ? (
                                                <Box component="img" src={row.goodsImgS} alt="" sx={PRODUCT_IMAGE_SX} />
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell>{row.brandName || '-'}</TableCell>
                                        <TableCell>{row.goodsName || '-'}</TableCell>
                                        <TableCell align="right">{Number(row.discountPrice || row.salePrice || 0).toLocaleString()}원</TableCell>
                                        <TableCell align="right">
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => onSelect(row)}
                                            >
                                                선택
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={5}>
                                            <Typography variant="body2" color="text.secondary">
                                                조회된 판매 중 상품이 없습니다.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                        {totalCount > 0 && (
                            <Pagination curPage={curPage} offset={OFFSET} totalCount={totalCount} setCurPage={setCurPage} />
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>닫기</Button>
            </DialogActions>
        </Dialog>
    )
}
